import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { Box, Card, CardBody, CardHeader, Tab, TabList, TabPanel, TabPanels, Tabs, Text, useColorModeValue } from "@chakra-ui/react";
import { motion } from "framer-motion";

import AdministrationFees from "./AdministrationFees";
import BillingInformation from "./BillingInformation";
import FinancialStatus from "./FinancialStatus";
import PersonalInformation from "./PersonalInformation";

const MotionBox = motion(Box);

export default function Owner() {
	const { id } = useParams();
	// Select the raw array so the memos below keep a stable reference across
	// re-renders that are not caused by a store update (typing in a modal, etc).
	const storeProperties = useSelector((state) => state.user?.user?.properties);
	const allProperties = useMemo(() => (storeProperties || []).filter((p) => !p.archived), [storeProperties]);
	const owner = useMemo(() => allProperties.find((p) => p.owner?.id === id)?.owner, [allProperties, id]);
	const properties = useMemo(
		() => (owner ? allProperties.filter((p) => owner.properties?.includes(p.id)) : []),
		[allProperties, owner],
	);
	const tabs = owner
		? [
				{ title: "Personal Information", component: <PersonalInformation owner={owner} /> },
				{ title: "Financial Status", component: <FinancialStatus owner={owner} properties={properties} /> },
				{ title: "Billing Information", component: <BillingInformation owner={owner} properties={properties} /> },
				{ title: "Administration Fees", component: <AdministrationFees owner={owner} properties={properties} /> },
			]
		: [];

	const bgColor = useColorModeValue("white", "gray.800");
	const borderColor = useColorModeValue("gray.200", "gray.700");
	const tabBg = useColorModeValue("gray.50", "gray.700");
	const activeTabBg = useColorModeValue("white", "gray.800");

	useEffect(() => {
		console.log("properties: ", properties);
		console.log("owner: ", owner);
	}, [owner, properties]);

	if (!owner) {
		return (
			<MotionBox initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
				<Card boxShadow="sm" borderRadius="lg" bg={bgColor} borderWidth="1px" borderColor={borderColor}>
					<CardHeader fontSize="2xl" fontWeight="bold">
						Owner Details
					</CardHeader>
					<CardBody>
						<Text>Owner not found.</Text>
					</CardBody>
				</Card>
			</MotionBox>
		);
	}

	return (
		<MotionBox initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
			<Card boxShadow="sm" borderRadius="lg" bg={bgColor} borderWidth="1px" borderColor={borderColor}>
				<CardHeader fontSize="2xl" fontWeight="bold" pb={6} borderBottomWidth="1px" borderColor={borderColor}>
					Owner Details
				</CardHeader>
				<CardBody pt={6}>
					<Tabs variant="enclosed" isFitted isLazy colorScheme="blue">
						<TabList overflowX="auto" overflowY="hidden" borderBottomWidth="2px" borderColor={borderColor} mb={4}>
							{tabs.map((tabItem, index) => (
								<Tab
									key={index}
									_selected={{
										bg: activeTabBg,
										borderColor: borderColor,
										borderBottomColor: activeTabBg,
										mb: "-2px",
										fontWeight: "semibold",
									}}
									py={3}
									bg={tabBg}
									borderRadius="md"
									borderTopRadius="md"
									mx={1}
									transition="all 0.2s"
									_hover={{ bg: activeTabBg }}
								>
									{tabItem.title}
								</Tab>
							))}
						</TabList>
						<TabPanels>
							{tabs.map((tabItem, index) => (
								<TabPanel key={index} px={0}>
									{tabItem.component}
								</TabPanel>
							))}
						</TabPanels>
					</Tabs>
				</CardBody>
			</Card>
		</MotionBox>
	);
}
